import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import AccountPickerContext from 'contexts/AccountPickerContext';
import CloudFunctionClientContext from 'contexts/CloudFunctionClientContext';
import ToastContext from 'contexts/ToastContext';
import {
  archiveAgentCheck,
  listAgentChecks,
  runAgentCheck,
  saveAgentCheck,
} from 'services/agentKb';

import { summaryOfRun } from './summariseEval';

// Evaluation checks for the current account. Reads and writes go through the
// insightsAgentKb endpoint (there is no client Firestore access to these
// collections). Provider-level so a "Run all" survives navigation, and so the
// full runs seen this session are kept for display without re-fetching.

interface SaveCheckParams {
  checkId?: string;
  question: string;
  purpose?: string;
  scope: AgentEval.Scope['kind'];
  products: AgentEval.ProductKey[];
  requires: AgentEval.DatasetConcept[];
  expectsChart?: boolean;
  ambiguous?: boolean;
  expected: AgentEval.Expectation;
  reportId?: string | null;
  context?: AgentEval.Context | null;
}

interface RunManyParams {
  checkIds: string[];
  target: AgentEval.RunTarget;
  onProgress?: (params: { done: number; total: number }) => void;
}

interface AgentEvalContextType {
  checks: AgentEval.ListedCheck[];
  account: {
    products: AgentEval.ProductKey[] | null;
    datasetTypes: string[] | null;
  };
  isLoading: boolean;
  rights: AgentKb.Rights;
  runningIds: Set<string>;
  // Full runs by checkId — everything run or viewed this session.
  runs: Record<string, AgentEval.Run>;
  refresh: () => Promise<void>;
  rememberRun: (run: AgentEval.Run) => void;
  saveCheck: (params: SaveCheckParams) => Promise<AgentEval.Check | null>;
  archiveCheck: (checkId: string) => Promise<boolean>;
  runOne: (params: {
    checkId: string;
    target: AgentEval.RunTarget;
  }) => Promise<AgentEval.Run | null>;
  // Sequential — each run is a full agent loop and account knowledge is cached,
  // so firing them at once would race. Cancellable between checks.
  runMany: (params: RunManyParams) => Promise<void>;
  stopRunMany: () => void;
}

const NO_RIGHTS: AgentKb.Rights = {
  canRead: false,
  canEdit: false,
  canPublish: false,
};

const NO_ACCOUNT: AgentEvalContextType['account'] = {
  products: null,
  datasetTypes: null,
};

export const AgentEvalContext = createContext<AgentEvalContextType>({
  checks: [],
  account: NO_ACCOUNT,
  isLoading: true,
  rights: NO_RIGHTS,
  runningIds: new Set(),
  runs: {},
  refresh: () => {
    throw new Error('refresh used outside AgentEvalProvider');
  },
  rememberRun: () => {
    throw new Error('rememberRun used outside AgentEvalProvider');
  },
  saveCheck: () => {
    throw new Error('saveCheck used outside AgentEvalProvider');
  },
  archiveCheck: () => {
    throw new Error('archiveCheck used outside AgentEvalProvider');
  },
  runOne: () => {
    throw new Error('runOne used outside AgentEvalProvider');
  },
  runMany: () => {
    throw new Error('runMany used outside AgentEvalProvider');
  },
  stopRunMany: () => {
    throw new Error('stopRunMany used outside AgentEvalProvider');
  },
});

const AgentEvalProvider = ({ children }: { children: ReactNode }) => {
  const { api } = useContext(CloudFunctionClientContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { showToast } = useContext(ToastContext);

  const [checks, setChecks] = useState<AgentEval.ListedCheck[]>([]);
  const [account, setAccount] = useState(NO_ACCOUNT);
  const [rights, setRights] = useState<AgentKb.Rights>(NO_RIGHTS);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [runningIds, setRunningIds] = useState<Set<string>>(new Set());
  const [runs, setRuns] = useState<Record<string, AgentEval.Run>>({});
  const cancelledRef = useRef<boolean>(false);

  const refresh = useCallback(async (): Promise<void> => {
    const result = await listAgentChecks(api);
    if (result.ok) {
      setChecks(result.data.checks);
      setAccount(result.data.account);
      setRights(result.data.rights);
    } else {
      showToast(result.error);
    }
    setIsLoading(false);
  }, [api, showToast]);

  useEffect(() => {
    setIsLoading(true);
    // Runs belong to the account they ran against.
    setRuns({});
    refresh();
  }, [refresh, selectedAccountId]);

  const rememberRun = useCallback((run: AgentEval.Run) => {
    setRuns((current) => ({ ...current, [run.checkId]: run }));
  }, []);

  const markRunning = useCallback((checkId: string, running: boolean) => {
    setRunningIds((current) => {
      const next = new Set(current);
      if (running) {
        next.add(checkId);
      } else {
        next.delete(checkId);
      }
      return next;
    });
  }, []);

  const saveCheck = useCallback(
    async (params: SaveCheckParams): Promise<AgentEval.Check | null> => {
      const result = await saveAgentCheck(api, params);
      if (!result.ok) {
        showToast(result.error);
        return null;
      }
      showToast(params.checkId ? 'Check updated' : 'Check created');
      // The saved document may change applicability, so re-list rather than
      // patching a half-known ListedCheck together on the client.
      await refresh();
      return result.data.check;
    },
    [api, showToast, refresh],
  );

  const archiveCheck = useCallback(
    async (checkId: string): Promise<boolean> => {
      const result = await archiveAgentCheck(api, { checkId });
      if (!result.ok) {
        showToast(result.error);
        return false;
      }
      setChecks((current) => current.filter((check) => check.id !== checkId));
      showToast('Check archived');
      return true;
    },
    [api, showToast],
  );

  const runOne = useCallback(
    async ({
      checkId,
      target,
    }: {
      checkId: string;
      target: AgentEval.RunTarget;
    }): Promise<AgentEval.Run | null> => {
      markRunning(checkId, true);
      try {
        const result = await runAgentCheck(api, { checkId, target });
        if (!result.ok) {
          showToast(result.error);
          return null;
        }
        const { run } = result.data;
        rememberRun(run);
        setChecks((current) =>
          current.map((check) =>
            check.id === checkId
              ? { ...check, lastRun: summaryOfRun(run) }
              : check,
          ),
        );
        return run;
      } finally {
        markRunning(checkId, false);
      }
    },
    [api, showToast, markRunning, rememberRun],
  );

  const stopRunMany = useCallback(() => {
    cancelledRef.current = true;
  }, []);

  const runMany = useCallback(
    async ({ checkIds, target, onProgress }: RunManyParams): Promise<void> => {
      cancelledRef.current = false;
      let done = 0;
      for (const checkId of checkIds) {
        if (cancelledRef.current) {
          break;
        }
        // eslint-disable-next-line no-await-in-loop
        await runOne({ checkId, target });
        done += 1;
        onProgress?.({ done, total: checkIds.length });
      }
    },
    [runOne],
  );

  const value = useMemo(
    () => ({
      checks,
      account,
      isLoading,
      rights,
      runningIds,
      runs,
      refresh,
      rememberRun,
      saveCheck,
      archiveCheck,
      runOne,
      runMany,
      stopRunMany,
    }),
    [
      checks,
      account,
      isLoading,
      rights,
      runningIds,
      runs,
      refresh,
      rememberRun,
      saveCheck,
      archiveCheck,
      runOne,
      runMany,
      stopRunMany,
    ],
  );

  return (
    <AgentEvalContext.Provider value={value}>
      {children}
    </AgentEvalContext.Provider>
  );
};

export default AgentEvalProvider;
